import React from 'react';
import { FiX } from 'react-icons/fi';
import { Sheet } from '../../../../../components/Sheet/Sheet';
import * as translator from '../../../../../utils/translator';
import useIsMobile from '../../../../../customHooks/useIsMobile';
import DesktopConsultationSummaryOverlay from './DesktopConsultationSummaryOverlay';
import { localeByLanguage } from '../careProfileAssets';

interface NutritionSummarySheetProps {
	isOpen: boolean;
	onClose: () => void;
	language: string;
	title: string;
	dateISO: string | null;
	doctorName?: string | null;
	onMessageDoctor?: () => void;
}

const NutritionSummarySheet: React.FC<NutritionSummarySheetProps> = ({ isOpen, onClose, language, title, dateISO, doctorName, onMessageDoctor }) => {
	const isMobile = useIsMobile();
	const locale = localeByLanguage[language] || 'en-US';
	const dateLabel = dateISO ? new Date(dateISO).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : null;
	const timeLabel = dateISO ? new Date(dateISO).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' }) : null;

	if (!isMobile) {
		return (
			<DesktopConsultationSummaryOverlay
				isOpen={isOpen}
				onClose={onClose}
				language={language}
				title={title}
				doctorName={doctorName}
				dateLabel={dateLabel}
				timeLabel={timeLabel}
				status="processing"
				onMessageDoctor={onMessageDoctor}
			/>
		);
	}

	return (
		<Sheet isOpen={isOpen} onClose={onClose}>
			<div id="care-profile-summary" className="flex flex-col w-full">
				<div className="relative flex items-center justify-center px-8 py-4 flex-shrink-0">
					<p className="text-[20px] font-semibold text-care-plus-heading text-center leading-[1.2]">{title}</p>
					<button
						id="care-profile-summary-close"
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="absolute right-2 flex items-center justify-center w-8 h-8 rounded-full border-none bg-transparent text-care-plus-heading cursor-pointer"
					>
						<FiX size={19} />
					</button>
				</div>

				<div className="px-5 pb-8">
					<div className="flex flex-col gap-y-3 mb-8">
						{doctorName && (
							<div className="flex items-baseline gap-x-2">
								<span className="text-sm text-[#616975] w-14 flex-shrink-0">{translator.translate(language, 'my_account', 'CareProfile/Booking/Doctor')}</span>
								<span className="text-[15px] font-semibold text-care-plus-heading">{doctorName}</span>
							</div>
						)}
						{dateLabel && (
							<div className="flex items-baseline gap-x-2">
								<span className="text-sm text-[#616975] w-14 flex-shrink-0">{translator.translate(language, 'my_account', 'CareProfile/Booking/Date')}</span>
								<span className="text-[15px] font-semibold text-care-plus-heading">{dateLabel}</span>
							</div>
						)}
						{timeLabel && (
							<div className="flex items-baseline gap-x-2">
								<span className="text-sm text-[#616975] w-14 flex-shrink-0">{translator.translate(language, 'my_account', 'CareProfile/Booking/Time')}</span>
								<span className="text-[15px] font-semibold text-care-plus-heading">{timeLabel}</span>
							</div>
						)}
					</div>

					{/* Pending state — the doctor's written guidelines are not available yet. */}
					<div className="flex flex-col items-center justify-center py-8 gap-y-4">
						<span className="w-9 h-9 rounded-full border-2 border-[#e7e4db] border-t-care-plus-orange animate-spin" />
						<p className="text-center text-[15px] text-[#99a3b2] max-w-[220px]">
							{translator.translate(language, 'my_account', 'CareProfile/Summary/Pending')}
						</p>
					</div>
				</div>
			</div>
		</Sheet>
	);
};

export default NutritionSummarySheet;
